import { useFormContext } from 'react-hook-form'
import { useQuery } from 'react-query'

import { toast } from 'sonner'
import { useMediaQuery } from 'usehooks-ts'

import { Button } from '@/components/ui/button'
import { PACKAGES } from '@/constants'
import { cn } from '@/lib/utils'
import { pricing } from '@/pages/customer/book-shelf'
import { BookExtendForm } from '@/pages/customer/extend-booking'
import bookingService from '@/services/booking-service'

interface NewBookingFormProps {
	selectedBooking: UserBooking
}

export const NewBookingForm = ({ selectedBooking }: NewBookingFormProps) => {
	const form = useFormContext<BookExtendForm>()

	const { data } = useQuery(
		'get-extend-feasibility',
		() => bookingService.getExtendFeasibility(selectedBooking.stand_id, selectedBooking.end),
		{
			refetchOnWindowFocus: false,
			onError: () => {
				toast.error('Error fetching booking feasibilty')
			},
		},
	)

	return (
		<div className="flex mt-[28px] lg:mt-16 flex-col items-center justify-center gap-5">
			<div className="flex w-full justify-center items-center gap-5 max-lg:flex-col">
				{data &&
					pricing
						.slice(0, 3)
						.map(price => (
							<PricingCard
								key={price.id}
								id={price.id}
								icon={price.icon}
								selectedPacket={form.watch('package')}
								title={price.title}
								feasibility={data}
								pricing={price.pricing}
								provision={price.provision}
							/>
						))}
			</div>
			<div className="flex w-full justify-center items-center gap-5 max-lg:flex-col">
				{data &&
					pricing
						.slice(3, 4)
						.map(price => (
							<PricingCard
								key={price.id}
								id={price.id}
								icon={price.icon}
								selectedPacket={form.watch('package')}
								title={price.title}
								pricing={price.pricing}
								feasibility={data}
								provision={price.provision}
							/>
						))}
			</div>
		</div>
	)
}

interface PricingCardProps {
	icon: React.ElementType
	id: number
	title: string
	pricing: number
	provision: number
	selectedPacket: string
	feasibility: {
		sevenDayOverlap: string
		fourteenDayOverlap: string
		twentyOneDayOverlap: string
		twentyEightDayOverlap: string
	}
}

const PricingCard = ({
	id,
	icon: Icon,
	title,
	pricing,
	provision,
	selectedPacket,
	feasibility,
}: PricingCardProps) => {
	const form = useFormContext<BookExtendForm>()
	const isSmallScreen = useMediaQuery('(max-width: 64rem)')

	const selectedPackage = PACKAGES.find(p => p.id === Number(id))!

	return (
		<div
			onClick={() => {
				if (
					(selectedPackage.duration === 7 && Number(feasibility.sevenDayOverlap) > 0) ||
					(selectedPackage.duration === 14 && Number(feasibility.fourteenDayOverlap) > 0) ||
					(selectedPackage.duration === 21 && Number(feasibility.twentyOneDayOverlap) > 0) ||
					(selectedPackage.duration === 28 && Number(feasibility.twentyEightDayOverlap) > 0)
				) {
					return
				}
				form.setValue('package', id.toString(), { shouldValidate: true })
			}}
			className={cn(
				'rounded-[16px] group hover:bg-primary cursor-pointer border border-secondary/50 max-lg:flex-1 max-lg:w-full lg:w-[300px] pt-4 pb-3 px-4 lg:p-5 flex flex-col',
				selectedPacket === id.toString() ? 'bg-primary' : 'bg-white',
				{
					'cursor-not-allowed':
						(selectedPackage.duration === 7 && Number(feasibility.sevenDayOverlap) > 0) ||
						(selectedPackage.duration === 14 && Number(feasibility.fourteenDayOverlap) > 0) ||
						(selectedPackage.duration === 21 && Number(feasibility.twentyOneDayOverlap) > 0) ||
						(selectedPackage.duration === 28 && Number(feasibility.twentyEightDayOverlap) > 0),
				},
			)}>
			<div className="flex items-center gap-2">
				<Icon
					className={cn(
						'size-[18px] group-hover:text-white lg:size-8',
						selectedPacket === id.toString() ? 'text-white' : 'text-secondary',
					)}
				/>
				<h3
					className={cn(
						'lg:text-xl font-extrabold group-hover:text-white',
						selectedPacket === id.toString() ? 'text-white' : 'text-secondary',
					)}>
					{title}
				</h3>
			</div>
			<div
				className={cn(
					'mb-5 mt-[15px] h-px group-hover:bg-[#E0E4EB1A]',
					selectedPacket === id.toString() ? 'bg-[#E0E4EB1A]' : 'bg-secondary/10',
				)}
			/>
			<div className="flex items-center gap-5">
				<h2
					className={cn(
						'text-2xl lg:text-[40px] font-extrabold group-hover:text-white',
						selectedPacket === id.toString() ? 'text-white' : 'text-secondary',
					)}>
					{pricing} <span className="text-xs lg:text-xl -ml-1 lg:-ml-2.5">€</span>
				</h2>
				<p
					className={cn(
						'text-sm font-medium group-hover:text-white/75',
						selectedPacket === id.toString() ? 'text-white/75' : 'text-secondary/50',
					)}>
					+ {provision}% Provision
				</p>
			</div>
			<Button
				size={isSmallScreen ? 'sm' : 'default'}
				type="button"
				className={cn(
					'mt-10 lg:mt-10 hover:bg-white/30 group-hover:text-white group-hover:border-0 group-hover:bg-white/30 rounded-lg font-extrabold',
					selectedPacket === id.toString()
						? 'text-white border-0 bg-white/30'
						: 'border border-primary bg-white text-primary',
				)}>
				Jetzt Buchen
			</Button>
		</div>
	)
}
